<template>
  <div class="home">
    <section class="hero">
      <h1>Peças 3D exclusivas</h1>
      <p>Conheça nossa coleção especial de objetos impressos em 3D. Nossos produtos trazem inovação, beleza e funcionalidade para o seu dia a dia. Explore os diversos modelos disponíveis e leve criatividade para seu ambiente!</p>
    </section>
    
    <section class="produtos-destaque">
      <h2>Produtos em destaque</h2>
      
      <div class="carrossel-container">
        <button @click="prevSlide" class="carrossel-btn prev-btn">&lt;</button>
        
        <div class="carrossel">
          <div class="carrossel-slide" :style="{ transform: `translateX(-${currentSlide * 100}%)` }">
            <div v-for="(grupo, index) in produtosAgrupados" :key="index" class="carrossel-grupo">
              <div v-for="produto in grupo" :key="produto.id" class="produto-card">
                <div class="produto-imagem">
                  <img :src="produto.imagens[0]" :alt="produto.nome">
                </div>
                <div class="produto-info">
                  <h3>{{ produto.nome }}</h3>
                  <p class="produto-descricao">{{ produto.descricao }}</p>
                  <p class="produto-preco">A partir de R$ {{ produto.precos[2].valor.toFixed(2) }}</p>
                  <router-link :to="`/produto/${produto.id}`" class="btn-detalhes" color="primary">
                    Ver detalhes
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <button @click="nextSlide" class="carrossel-btn next-btn">&gt;</button>
      </div>
      
      <div class="carrossel-indicadores">
        <span 
          v-for="(_, index) in produtosAgrupados" 
          :key="index" 
          :class="{ 'ativo': currentSlide === index }"
          @click="goToSlide(index)"
        ></span>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { produtos } from '@/data/produtos'

export default {
  setup() {
    const currentSlide = ref(0)
    const isMobile = ref(false)
    const itemsPerSlide = computed(() => isMobile.value ? 1 : 3)
    
    // Verifica o tamanho da tela
    const checkScreenSize = () => {
      isMobile.value = window.innerWidth < 768
    }
    
    // Agrupa os produtos baseado no tamanho da tela
    const produtosAgrupados = computed(() => {
      const grupos = []
      const itemsPerGroup = itemsPerSlide.value
      
      for (let i = 0; i < produtos.length; i += itemsPerGroup) {
        grupos.push(produtos.slice(i, i + itemsPerGroup))
      }
      return grupos
    })
    
    const totalSlides = computed(() => produtosAgrupados.value.length)
    
    const nextSlide = () => {
      if (currentSlide.value < totalSlides.value - 1) {
        currentSlide.value++
      } else {
        currentSlide.value = 0 // volta para o início
      }
    }
    
    const prevSlide = () => {
      if (currentSlide.value > 0) {
        currentSlide.value--
      } else {
        currentSlide.value = totalSlides.value - 1 // vai para o último
      }
    }
    
    const goToSlide = (index) => {
      currentSlide.value = index
    }
    
    // Habilita navegação por swipe para dispositivos touch
    const touchStartX = ref(0)
    const touchEndX = ref(0)
    
    const handleTouchStart = (e) => {
      touchStartX.value = e.touches[0].clientX
    }
    
    const handleTouchMove = (e) => {
      touchEndX.value = e.touches[0].clientX
    }
    
    const handleTouchEnd = () => {
      const difference = touchStartX.value - touchEndX.value
      
      if (difference > 50) {
        // Swipe para esquerda (próximo slide)
        nextSlide()
      } else if (difference < -50) {
        // Swipe para direita (slide anterior)
        prevSlide()
      }
    }
    
    // Adiciona e remove event listeners
    onMounted(() => {
      checkScreenSize()
      window.addEventListener('resize', checkScreenSize)
      
      const carrossel = document.querySelector('.carrossel')
      if (carrossel) {
        carrossel.addEventListener('touchstart', handleTouchStart, { passive: true })
        carrossel.addEventListener('touchmove', handleTouchMove, { passive: true })
        carrossel.addEventListener('touchend', handleTouchEnd, { passive: true })
      }
    })
    
    onUnmounted(() => {
      window.removeEventListener('resize', checkScreenSize)
      
      const carrossel = document.querySelector('.carrossel')
      if (carrossel) {
        carrossel.removeEventListener('touchstart', handleTouchStart)
        carrossel.removeEventListener('touchmove', handleTouchMove)
        carrossel.removeEventListener('touchend', handleTouchEnd)
      }
    })
    
    // Iniciar autoplay (opcional)
    const autoplayInterval = ref(null)
    
    const startAutoplay = () => {
      autoplayInterval.value = setInterval(() => {
        nextSlide()
      }, 5000) // muda a cada 5 segundos
    }
    
    const stopAutoplay = () => {
      if (autoplayInterval.value) {
        clearInterval(autoplayInterval.value)
      }
    }
    
    onMounted(() => {
      startAutoplay()
    })
    
    onUnmounted(() => {
      stopAutoplay()
    })
    
    return {
      produtosAgrupados,
      currentSlide,
      nextSlide,
      prevSlide,
      goToSlide,
      isMobile
    }
  }
}
</script>

<style scoped>
.hero {
  background-color: #f0f0f0;
  padding: 60px 20px;
  text-align: center;
  margin-bottom: 40px;
}

.hero h1 {
  font-size: 2.5rem;
  margin-bottom: 20px;
  color: #333;
}

.hero p {
  font-size: 1.2rem;
  color: #666;
}

.produtos-destaque {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.produtos-destaque h2 {
  text-align: center;
  margin-bottom: 30px;
  font-size: 1.8rem;
  color: #333;
}

.carrossel-container {
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 auto;
  max-width: 1000px;
}

.carrossel {
  width: 100%;
  overflow: hidden;
  position: relative;
  touch-action: pan-y;
}

.carrossel-slide {
  display: flex;
  transition: transform 0.5s ease;
  width: 100%;
}

.carrossel-grupo {
  display: flex;
  min-width: 100%;
  gap: 20px;
}

.produto-card {
  flex: 1;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.produto-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.produto-imagem {
  height: 200px;
  overflow: hidden;
}

.produto-imagem img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.produto-card:hover .produto-imagem img {
  transform: scale(1.05);
}

.produto-info {
  padding: 15px;
}

.produto-info h3 {
  margin-top: 0;
  font-size: 1.2rem;
  margin-bottom: 10px;
}

.produto-descricao {
  color: #666;
  margin-bottom: 10px;
  font-size: 0.9rem;
  height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.produto-preco {
  font-weight: bold;
  color: #2ecc71;
  margin-bottom: 15px;
}

.btn-detalhes {
  display: inline-block;
  background-color: rgb(22,28,104);
  color: white;
  text-decoration: none;
  padding: 8px 15px;
  border-radius: 4px;
  transition: background-color 0.3s;
  text-align: center;
  width: 100%;
}

.btn-detalhes:hover {
  background-color:rgb(63, 67, 125);
}

.carrossel-btn {
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  transition: background-color 0.3s;
}

.carrossel-btn:hover {
  background-color: rgba(0, 0, 0, 0.7);
}

.prev-btn {
  margin-right: 10px;
}

.next-btn {
  margin-left: 10px;
}

.carrossel-indicadores {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.carrossel-indicadores span {
  display: inline-block;
  width: 12px;
  height: 12px;
  background-color: #ddd;
  border-radius: 50%;
  margin: 0 5px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.carrossel-indicadores span.ativo {
  background-color: #3498db;
}

/* Estilos responsivos para dispositivos móveis */
@media (max-width: 767px) {
  .hero h1 {
    font-size: 1.8rem;
  }
  
  .hero p {
    font-size: 1rem;
  }
  
  .produtos-destaque h2 {
    font-size: 1.5rem;
  }
  
  .carrossel-container {
    padding: 0 10px;
  }
  
  .carrossel-btn {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }
  
  .carrossel-grupo {
    gap: 10px;
  }
  
  .produto-imagem {
    height: 160px;
  }
  
  .produto-info {
    padding: 10px;
  }
  
  .produto-info h3 {
    font-size: 1rem;
  }
  
  .produto-descricao {
    font-size: 0.8rem;
  }
  
  .btn-detalhes {
    padding: 6px 12px;
    font-size: 0.9rem;
  }
}
</style>